export interface ErrorResponse {
  status: number;
  headers?: Headers; // Optional headers property
}

type Interval = number | ((error: ErrorResponse) => number);

const isErrorResponse = (error: any): error is ErrorResponse => {
  return error && typeof error.status === "number";
};

const isFailedToFetch = (error: any): boolean => {
  return error instanceof TypeError;
};

const isRetriable = (error: ErrorResponse): boolean => {
  return (
    error.status < 100 || // network error: https://fetch.spec.whatwg.org/#concept-network-error
    (error.status >= 500 && error.status < 600) || // server errors
    error.status == 429 || // too many requests
    error.status == 425 // too early
  );
};

const normalizeError = (error: any): ErrorResponse | null => {
  if (isErrorResponse(error)) {
    return error;
  }

  if (isFailedToFetch(error)) {
    return { status: 0 };
  }

  return null;
};

const resolveInterval = (interval: Interval, error: ErrorResponse): number => {
  return typeof interval === "function" ? interval(error) : interval;
};

export const useApiRetry = (
  defaultMaxRetries = 3,
  defaultInterval: Interval = 3000
) => {
  const retry = async <T>(
    fn: () => Promise<T>,
    interval: Interval = defaultInterval,
    maxRetries = defaultMaxRetries
  ): Promise<T> => {
    let lastError: any = null;

    for (let retries = 0; retries <= maxRetries; retries++) {
      try {
        return await fn();
      } catch (error: any) {
        const normalizedError = normalizeError(error);

        if (normalizedError && isRetriable(normalizedError)) {
          lastError = error;
          await new Promise((resolve) =>
            setTimeout(resolve, resolveInterval(interval, normalizedError))
          );
        } else {
          throw error;
        }
      }
    }

    throw lastError;
  };

  return { retry };
};
